import { FormikErrors, FormikHelpers } from "formik";
import { deepMerge } from "@common/helpers/objects";
import type { InputSize as DsInputSize } from "@design-system/Input/Input.types";
import { ParseParams, ZodSchema } from "zod";

export type InputSize = "xxs" | "xs" | "sm" | "lg";

/** Legacy form-control size → DS Input size (undefined / lg / sm → xl, xs → lg, xxs → md). */
export const toDsInputSize = (size?: InputSize): DsInputSize => {
    switch (size) {
        case "xxs":
            return "md";
        case "xs":
            return "lg";
        case "sm":
        default:
            return "xl";
    }
};

interface SubmitHelperOptions<V> {
    handler(values: V, formikHelpers: FormikHelpers<V>): Promise<void>;
    failureHandler?(
        error: Error,
        values: V,
        formikHelpers: FormikHelpers<V>,
    ): Promise<void>;
    onSuccess?: () => Promise<void>;
    loading?: boolean;
    setLoading?: (value: boolean) => void;
}

export function submitHelper<Values>(
    options: SubmitHelperOptions<Values>,
): (values: Values, formikHelpers: FormikHelpers<Values>) => Promise<void> {
    let isRunning = false;

    return async (values: Values, formikHelpers: FormikHelpers<Values>) => {
        if (options.loading || isRunning) {
            return;
        }

        isRunning = true;
        options.setLoading?.(true);

        try {
            await options.handler(values, formikHelpers);
            await options.onSuccess?.();
        } catch (e) {
            await options.failureHandler?.(e as Error, values, formikHelpers);
        } finally {
            options.setLoading?.(false);
            isRunning = false;
        }
    };
}

// Removed unused export: getErrorsSync(validationSchema: Schema<TFormSchema>, values: TFormSchema) - see PR #4802 for history

type FormikFieldError =
    | string
    | Array<FormikFieldError | null | undefined>
    | { [key: string]: FormikFieldError | null | undefined };

/**
 * Formik error gating for a field: show it once the user has left the field
 * or tried to submit. Use it to drive a control's `invalid` outside FormikField.
 */
export const shouldShowFieldError = ({
    error,
    touched,
    submitCount,
}: {
    error: FormikFieldError | null | undefined;
    touched: boolean | undefined;
    submitCount: number;
}): boolean => Boolean(error && (touched || submitCount > 0));

export const flattenFormError = (error: FormikFieldError): string => {
    const collect = (value: FormikFieldError | null | undefined): string[] => {
        if (value == null) {
            return [];
        }

        if (typeof value === "string") {
            return value ? [value] : [];
        }

        const entries = Array.isArray(value) ? value : Object.values(value);

        return entries.flatMap(collect);
    };

    return [...new Set(collect(error))].join(", ");
};

export const validateFormikWithZodSchema =
    <T>(schema: ZodSchema<T>, params?: Partial<ParseParams>) =>
    (values: unknown): FormikErrors<T> => {
        const result = schema.safeParse(values, params);
        if (result.success) {
            return {};
        }

        return result.error.issues.reduce(
            (acc, curr) =>
                deepMerge(
                    acc,
                    curr.path.reduceRight(
                        (errors, pathSegment) => ({
                            [pathSegment]: !Object.keys(errors).length
                                ? curr.message
                                : errors,
                        }),
                        {},
                    ),
                ),
            {},
        );
    };
